/**
 * Fábrica central de query keys do TanStack Query.
 *
 * Regras:
 * - Toda key nasce aqui; nunca usar arrays literais soltos nos componentes.
 * - Hierarquia: [domínio, ...escopo]. Invalidar o domínio inteiro com
 *   queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }).
 * - Filtros/paginação entram como objeto no último segmento para cache por variação.
 */
export const queryKeys = {
    account: {
        all: ["account"] as const,
        me: () => [...queryKeys.account.all, "me"] as const,
        tenants: () => [...queryKeys.account.all, "tenants"] as const,
        currentTenant: () => [...queryKeys.account.all, "currentTenant"] as const,
        publicTenant: (slug: string) => [...queryKeys.account.all, "publicTenant", slug] as const,
        invitation: (token: string) => [...queryKeys.account.all, "invitation", token] as const,
    },
    toconline: {
        all: ["toconline"] as const,
        availability: () => [...queryKeys.toconline.all, "availability"] as const,
        settings: () => [...queryKeys.toconline.all, "settings"] as const,
        tenantSettings: (tenantId: string) => [...queryKeys.toconline.all, "settings", "tenant", tenantId] as const,
    },
    files: {
        all: ["files"] as const,
        usage: () => [...queryKeys.files.all, "usage"] as const,
        tenantsUsage: () => [...queryKeys.files.all, "usage", "tenants"] as const,
    },
    projects: {
        all: ["projects"] as const,
        lists: () => [...queryKeys.projects.all, "list"] as const,
        statusStats: () => [...queryKeys.projects.all, "status-stats"] as const,
        recentDiaries: (day: string) => [...queryKeys.projects.all, "recent-diaries", day] as const,
        list: (filters: Record<string, unknown>) => [...queryKeys.projects.lists(), filters] as const,
        detail: (projectId: string) => [...queryKeys.projects.all, "detail", projectId] as const,
        diary: (projectId: string) => [...queryKeys.projects.all, "diary", projectId] as const,
        diaryPage: (projectId: string, filters: Record<string, unknown>) =>
            [...queryKeys.projects.diary(projectId), filters] as const,
        steps: (projectId: string) => [...queryKeys.projects.all, "steps", projectId] as const,
        planningExtras: (projectId: string) => [...queryKeys.projects.all, "planning-extras", projectId] as const,
        stepsPage: (projectId: string, page: number) =>
            [...queryKeys.projects.steps(projectId), { page }] as const,
        financial: (projectId: string) => [...queryKeys.projects.all, "financial", projectId] as const,
        financialCosts: (projectId: string) => [...queryKeys.projects.financial(projectId), "costs"] as const,
        financialReceivements: (projectId: string) =>
            [...queryKeys.projects.financial(projectId), "receivements"] as const,
        financialChart: (projectId: string) => [...queryKeys.projects.financial(projectId), "chart"] as const,
        areaMetrics: (projectId: string) => [...queryKeys.projects.all, "area-metrics", projectId] as const,
        sections: (projectId: string) => [...queryKeys.projects.all, "sections", projectId] as const,
        financialSection: (projectId: string) => [...queryKeys.projects.all, "financial-section", projectId] as const,
        checkinRecords: (projectId: string, filters: Record<string, unknown>) =>
            [...queryKeys.projects.all, "checkin-records", projectId, filters] as const,
        checkinRecord: (projectId: string, recordId: string) =>
            [...queryKeys.projects.all, "checkin-record", projectId, recordId] as const,
        nextSteps: (projectId: string, flags: Record<string, boolean>) =>
            [...queryKeys.projects.all, "next-steps", projectId, flags] as const,

        tasksByStep: (projectId: string, stepId: string) =>
            [...queryKeys.projects.all, "tasks", projectId, stepId] as const,
        taskDetail: (projectId: string, stepId: string, taskId: string) =>
            [...queryKeys.projects.all, "task", projectId, stepId, taskId] as const,
        taskEvidenceMedia: (projectId: string, stepId: string, taskId: string, fileId: string) =>
            [...queryKeys.projects.all, "task-evidence", projectId, stepId, taskId, fileId] as const,
        team: (projectId: string) => [...queryKeys.projects.all, "team", projectId] as const,
        assignableUsers: (filters: Record<string, unknown>) =>
            [...queryKeys.projects.all, "assignable-users", filters] as const,
        roles: () => [...queryKeys.projects.all, "roles"] as const,
        workers: (projectId: string) => [...queryKeys.projects.all, "workers", projectId] as const,
        workerTasks: (projectId: string, username: string) =>
            [...queryKeys.projects.all, "worker-tasks", projectId, username] as const,
    },
    me: {
        all: ["me"] as const,
        tasks: (filters: Record<string, unknown>) => [...queryKeys.me.all, "tasks", filters] as const,
        taskDetail: (taskId: string) => [...queryKeys.me.all, "task", taskId] as const,
        face: () => [...queryKeys.me.all, "face"] as const,
        faceImage: () => [...queryKeys.me.all, "face", "image"] as const,
        dailyChecks: (projectId: string, date: string) => [...queryKeys.me.all, "daily-checks", projectId, date] as const,
    },
    stocks: {
        all: ["stocks"] as const,
        list: () => [...queryKeys.stocks.all, "list"] as const,
    },
    suppliers: {
        all: ["suppliers"] as const,
        options: () => [...queryKeys.suppliers.all, "options"] as const,
        // `size` entra na key porque nem todo consumidor quer a lista inteira:
        // alguns truncam em 100 de propósito. Sem isso, o primeiro a montar
        // definiria o tamanho para todos os outros.
        list: (size: number) => [...queryKeys.suppliers.all, "list", { size }] as const,
        search: (term: string) => [...queryKeys.suppliers.all, "search", term] as const,
    },
    costs: {
        all: ["costs"] as const,
        detail: (id: string) => [...queryKeys.costs.all, "detail", id] as const,
        attachments: (id: string) => [...queryKeys.costs.all, "attachments", id] as const,
    },
    fileAccess: {
        all: ["file-access"] as const,
        detail: (source: object, fileId: string) => [...queryKeys.fileAccess.all, source, fileId] as const,
        history: (source: object, fileId: string) => [...queryKeys.fileAccess.all, "history", source, fileId] as const,
    },
    attachments: {
        all: ["attachments"] as const,
        byOwner: (basePath: string) => [...queryKeys.attachments.all, basePath] as const,
    },
    categories: {
        all: ["categories"] as const,
        list: (size: number) => [...queryKeys.categories.all, "list", { size }] as const,
        search: (term: string) => [...queryKeys.categories.all, "search", term] as const,
        detail: (id: string) => [...queryKeys.categories.all, "detail", id] as const,
    },
    profileImages: {
        all: ["profile-images"] as const,
        byUsername: (username: string) => [...queryKeys.profileImages.all, username] as const,
        bySystemProfile: (systemProfileId: string) =>
            [...queryKeys.profileImages.all, "system-profile", systemProfileId] as const,
    },
    documentImports: {
        all: ["document-imports"] as const,
        lists: () => [...queryKeys.documentImports.all, "list"] as const,
        list: (filters: Record<string, unknown>) => [...queryKeys.documentImports.lists(), filters] as const,
        detail: (id: string) => [...queryKeys.documentImports.all, "detail", id] as const,
    },
    storages: {
        all: ["storages"] as const,
        detail: (storageId: string) => [...queryKeys.storages.all, "detail", storageId] as const,
        products: (storageId: string) => [...queryKeys.storages.all, "products", storageId] as const,
        movements: (storageId: string) => [...queryKeys.storages.all, "movements", storageId] as const,
    },
    purchaseRequests: {
        all: ["purchase-requests"] as const,
        byProject: (projectId: string) => [...queryKeys.purchaseRequests.all, "project", projectId] as const,
        byStorage: (storageId: string) => [...queryKeys.purchaseRequests.all, "storage", storageId] as const,
        orderItems: (scope: string, listId: string | undefined, page: number) =>
            [...queryKeys.purchaseRequests.all, "order-items", scope, listId, page] as const,
    },
    users: {
        all: ["users"] as const,
        options: (search: string) => [...queryKeys.users.all, "options", search] as const,
        label: (username: string) => [...queryKeys.users.all, "label", username] as const,
        professions: () => [...queryKeys.users.all, "professions"] as const,
        detail: (username: string) => [...queryKeys.users.all, "detail", username] as const,
        wallet: (username: string) => [...queryKeys.users.all, "wallet", username] as const,
    },
    clients: {
        all: ["clients"] as const,
        options: (search: string) => [...queryKeys.clients.all, "options", search] as const,
        label: (id: string) => [...queryKeys.clients.all, "label", id] as const,
    },
    financial: {
        all: ["financial"] as const,
    },
    notifications: {
        all: ["notifications"] as const,
    },
    documentExtractions: {
        all: ["document-extractions"] as const,
        list: (filters: Record<string, unknown>) => [...queryKeys.documentExtractions.all, "list", filters] as const,
        detail: (id: string) => [...queryKeys.documentExtractions.all, "detail", id] as const,
    },
} as const;
